import { Fragment, type ReactNode } from "react";
import {
  QR_BILL_LABELS, qrAccountDisplay, qrAddressLines, qrAmountDisplay, qrReferenceDisplay, qrSvgPath, swissQrCode, type SwissQrBill,
} from "@talentkontor/domain";

/**
 * Schweizer QR-Zahlteil in der Beleg-Ansicht (InvoicePaper): dieselben Angaben wie im PDF (swiss-qr-bill.ts, invoice-pdf.ts),
 * Empfangsschein links, Zahlteil mit Swiss QR Code und Schweizer Kreuz rechts. Der Code steht immer auf Weiß (auch im
 * Dunkelmodus lesbar); auf schmalen Bildschirmen stehen beide Teile untereinander.
 */
export function QrZahlteil({ bill }: { bill: SwissQrBill }) {
  const lb = QR_BILL_LABELS[bill.language === "en" ? "en" : "de"];
  const qr = swissQrCode(bill);
  const ref = bill.referenceType === "NON" ? null : qrReferenceDisplay(bill.referenceType, bill.reference);
  // IBAN und Referenz brechen nur zwischen den Zifferngruppen um, nie mitten in einer (wie im PDF)
  const gruppen = (s: string): ReactNode => s.split(" ").map((g, i) => <Fragment key={i}>{i ? " " : ""}<span className="tk-qr-gruppe">{g}</span></Fragment>);
  const creditor: ReactNode[] = [gruppen(qrAccountDisplay(bill.account)), ...qrAddressLines(bill.creditor)];
  const debtor = bill.debtor ? qrAddressLines(bill.debtor) : null;
  const amount = bill.amountCents == null ? null : qrAmountDisplay(bill.amountCents);
  // Kreuz: 7 mm von 46 mm (weißer Rand 0,5 mm, schwarzes Quadrat 6 mm)
  const u = qr.size / 46;
  const c = qr.size / 2;
  const block = (head: string, lines: ReactNode[]) => (
    <p><strong>{head}</strong>{lines.map((l, i) => <span key={i}><br />{l}</span>)}</p>
  );
  return (
    <section className="tk-qrbill" aria-label={lb.paymentPart} lang={bill.language}>
      <small className="tk-qrbill-cut">✂ {lb.separate}</small>
      <div className="tk-qrbill-grid">
        <div className="tk-qrbill-receipt">
          <h3>{lb.receipt}</h3>
          {block(lb.account, creditor)}
          {ref && block(lb.reference, [gruppen(ref)])}
          {debtor ? block(lb.payableBy, debtor) : <p><strong>{lb.payableByBlank}</strong><span className="tk-qrbill-blank" /></p>}
          <p className="row"><span><strong>{lb.currency}</strong><br />{bill.currency}</span><span><strong>{lb.amount}</strong><br />{amount ?? "…"}</span></p>
        </div>
        <div className="tk-qrbill-pay">
          <h3>{lb.paymentPart}</h3>
          <svg className="tk-qrbill-code" viewBox={`-4 -4 ${qr.size + 8} ${qr.size + 8}`} role="img" aria-label="Swiss QR Code">
            <rect x={-4} y={-4} width={qr.size + 8} height={qr.size + 8} fill="#fff" />
            <path d={qrSvgPath(qr)} fill="#000" shapeRendering="crispEdges" />
            <rect x={c - 3.5 * u} y={c - 3.5 * u} width={7 * u} height={7 * u} fill="#fff" />
            <rect x={c - 3 * u} y={c - 3 * u} width={6 * u} height={6 * u} fill="#000" />
            <rect x={c - (7 / 12) * u} y={c - (35 / 18) * u} width={(7 / 6) * u} height={(35 / 9) * u} fill="#fff" />
            <rect x={c - (35 / 18) * u} y={c - (7 / 12) * u} width={(35 / 9) * u} height={(7 / 6) * u} fill="#fff" />
          </svg>
          <p className="row"><span><strong>{lb.currency}</strong><br />{bill.currency}</span><span><strong>{lb.amount}</strong><br />{amount ?? "…"}</span></p>
        </div>
        <div className="tk-qrbill-info">
          {block(lb.account, creditor)}
          {ref && block(lb.reference, [gruppen(ref)])}
          {bill.message && block(lb.additional, [bill.message])}
          {debtor ? block(lb.payableBy, debtor) : <p><strong>{lb.payableByBlank}</strong><span className="tk-qrbill-blank" /></p>}
        </div>
      </div>
    </section>
  );
}
